import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { ElementType, ReactNode } from 'react';
import {
  ArrowRight,
  BarChart3,
  Brain,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  Compass,
  Layers3,
  Rocket,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';

export interface PresentationPRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vision' | 'essence' | 'raci' | 'cadre' | 'client' | 'bz' | 'roadmap';

const tabs: { id: TabId; label: string; icon: ElementType }[] = [
  { id: 'vision', label: 'Vision', icon: Sparkles },
  { id: 'essence', label: 'Essence', icon: Compass },
  { id: 'raci', label: 'RACI', icon: Users },
  { id: 'cadre', label: 'Gouv. & Cadre', icon: Layers3 },
  { id: 'client', label: 'Client', icon: Compass },
  { id: 'bz', label: 'BZ', icon: BriefcaseBusiness },
  { id: 'roadmap', label: 'Roadmap', icon: CalendarClock },
];

const macroObjectives = [
  'Formaliser et démontrer une amélioration observable et mesurable de la qualité, de l’efficacité et de la valeur ajoutée de nos activités/opérations/services/livrables pour l’infogérance des actifs TI de nos clients.',
  'Formaliser et démontrer une amélioration observable et mesurable de la qualité, de l’efficacité et de la valeur ajoutée de nos solutions d’affaire pour les activités opérationnelles (métier) de nos clients.',
  'Formaliser et démontrer une amélioration observable et mesurable de la qualité, de l’efficacité et de la valeur ajoutée de nos activités/opérations/services/livrables internes pour tous les département de BZ.',
  'Transition de l’idéologie actuelle des ventes (opportuniste et myopique, centrée sur des objectifs de ventes) à un accompagnement/partenariat TI concerté et centré/justifié sur les besoins d’affaires et le budget annuel récurrent du client.',
];

const governancePillars = [
  'Vision et stratégie PRIsME',
  'Accompagnement efficace, personnalisé et centré sur les besoins d’affaires du client',
  'Adoption interne et externe de l’idéologie PRIsME',
  'Gestion de carrière',
  'Rédaction et gestion des documents',
  'Gestion de projet',
  'Gestion de la connaissance (LEX)',
  'Gestion des réunions',
  'Visualisation des indicateurs de performance et de maturité',
];

const essenceItems = [
  {
    title: 'Source de vérité',
    body: 'PRIsME Master centralise les standards, cadres, méthodes, documents et connaissances validés par BZ. Les équipes partent d’une base commune au lieu de recréer, chercher ou interpréter chacune de leur côté.',
    proof: 'Même langage, mêmes standards, meilleure cohérence.',
    tone: 'from-cyan-500/25 to-cyan-500/5',
  },
  {
    title: 'Méthode de livraison',
    body: 'PRIsME transforme l’expertise BZ en une mécanique répétable : prise de besoin, analyse, génération assistée par IA, validation expert, livrables, suivi et amélioration continue.',
    proof: 'On livre plus vite sans perdre la personnalisation.',
    tone: 'from-emerald-500/25 to-emerald-500/5',
  },
  {
    title: 'Cockpit de gouvernance',
    body: 'PRIsME rend visibles les priorités, les rôles, les décisions, les risques, les tâches et la maturité. Le comité ne pilote plus avec des impressions : il pilote avec une lecture structurée et mesurable.',
    proof: 'Les décisions deviennent traçables, comparables et actionnables.',
    tone: 'from-violet-500/25 to-violet-500/5',
  },
  {
    title: 'Moteur de différenciation client',
    body: 'PRIsME donne au client une expérience plus professionnelle : compréhension de sa maturité, roadmap claire, livrables tangibles, preuves de progrès et accompagnement orienté besoins d’affaires.',
    proof: 'BZ devient plus crédible, plus scalable et plus difficile à copier.',
    tone: 'from-amber-500/25 to-amber-500/5',
  },
];

const clientFrameworkPillars = [
  {
    number: '01',
    title: 'Gouvernance, stratégie & organisation',
    detail: 'Cap, rôles, décisions, comités et priorités.',
    tone: 'from-fuchsia-500/25 to-fuchsia-500/5',
  },
  {
    number: '02',
    title: 'Architecture, infrastructure & cloud',
    detail: 'Fondations technologiques, résilience et capacité.',
    tone: 'from-blue-500/25 to-blue-500/5',
  },
  {
    number: '03',
    title: 'Applications, données & intégrations',
    detail: 'Patrimoine applicatif, données, flux et ownership.',
    tone: 'from-teal-500/25 to-teal-500/5',
  },
  {
    number: '04',
    title: 'Services, opérations & expérience utilisateur',
    detail: 'Support, incidents, changements et qualité perçue.',
    tone: 'from-amber-500/25 to-amber-500/5',
  },
  {
    number: '05',
    title: 'Cybersécurité, risque & conformité',
    detail: 'Protection, risques, contrôles, conformité et résilience.',
    tone: 'from-rose-500/25 to-rose-500/5',
  },
  {
    number: '06',
    title: 'Transformation, innovation & IA',
    detail: 'Automatisation, IA, modernisation et valeur mesurable.',
    tone: 'from-violet-500/25 to-violet-500/5',
  },
];

const clientFrameworkTransversals = [
  {
    letter: 'A',
    title: 'Gestion des actifs & parties prenantes',
    detail: 'Inventaire, owners, responsabilités, dépendances et traçabilité.',
    tone: 'border-emerald-300/25 bg-emerald-500/10 text-emerald-100',
  },
  {
    letter: 'B',
    title: 'Gestion de projet & amélioration continue',
    detail: 'Roadmap, jalons, tâches, RACI, post-mortem et maturité.',
    tone: 'border-cyan-300/25 bg-cyan-500/10 text-cyan-100',
  },
];

const raciRoles = [
  'Dany Bourassa',
  'Eric Hains',
  'Francis Marquis',
  'Jean-Michel Caouette',
  'Jean-Pierre Leclerc',
  'Stéphane Paradis',
  'AI',
  'Directeur de compte',
  'Éric Durand',
  'Actionnaires / gestionnaire',
  'Philippe Bolduc',
  'Mikael Drouin-Bouchard',
];

const raciRows = [
  ['Vision et Stratégie', 'A', 'C', 'I', 'I', 'I', 'C', 'C', '?', '', 'I', '', ''],
  ['Gouvernance & décision', 'A', 'R', 'I', 'I', 'I', 'C', 'C', '?', '', 'I', '', ''],
  ['Accompagnement client par le directeur TI', 'I', 'A', 'R', 'R', 'I', 'C', 'C', '?', '', 'I', '', ''],
  ['Développement PRIsME employé et département TI', 'C', 'C', 'I', 'I', 'A', 'C', 'C', '?', 'C', 'I', '', ''],
  ['Rigueur, Qualité, KPI et KMI', 'C', 'A', 'I', 'I', 'I', 'R', 'C', '?', '', 'I', '', ''],
  ['Développement PRIsME Client', 'I', 'A', 'I', 'R', 'I', 'C', 'C', '?', 'C', 'I', '', ''],
  ['Communication interne', 'I', 'A', 'C', 'C', 'C', 'C', 'C', '?', '', 'I', '', ''],
  ['Gestion des réunions TI', 'I', 'A', 'R', 'I', 'I', 'C', 'C', '?', '', 'I', '', ''],
  ['Gestion documentaire TI', 'I', 'A', 'I', 'R', 'I', 'C', 'C', '?', '', 'I', '', ''],
  ['Formation et adoption TI', 'I', 'A', 'R', 'R', 'R', 'C', 'C', '?', '', 'I', '', ''],
  ['Gestion de projet TI', 'R', 'A', 'C', 'C', 'C', 'C', 'C', '?', '', 'I', 'R', ''],
  ['Gestion des connaissances TI', 'C', 'A', 'R', 'C', 'R', 'C', 'C', '?', 'I', 'I', '', ''],
  ['Gestion de carrière TI', 'A', 'C', 'C', 'C', 'R', 'C', 'C', '?', '', 'I', '', ''],
  ['Gestion des subventions disponibles', 'I', 'A', 'C', 'C', 'I', 'C', 'C', '?', '', 'I', '', 'R'],
];

const clientJourney = [
  {
    title: 'Photo de maturité',
    body: 'Montrer au client où il est aujourd’hui, ce qui progresse et ce qui mérite un investissement prioritaire.',
  },
  {
    title: 'Directeur TI',
    body: 'Un point de contact principal, impartial, orienté besoins d’affaires et stratégie 5 ans.',
  },
  {
    title: 'Gouvernance',
    body: 'Encadrement des priorités, budget, décisions, risques, contraintes de temps et responsabilités.',
  },
  {
    title: 'Solutions d’affaires',
    body: 'Relier les interventions TI aux opérations, aux gains de productivité et aux opportunités IA.',
  },
];

const bzCapabilities = [
  'PRIsME Client pour structurer les comités TI, les photos de maturité et les plans d’action.',
  'Gestion de projets pour convertir les idées en tâches stratégiques, tactiques et opérationnelles.',
  'Gestion documentaire pour créer, standardiser, livrer et améliorer les documents BZ.',
  'Gestion de connaissance avec LEX et les agents IA pour retrouver et exploiter le savoir BZ.',
  'Gestion des rencontres pour capter décisions, actions et suivis dans une logique de rigueur.',
];

const roadmapItems = [
  ['Gouvernance', 'Refonte du comité PRIsME 2.0, objectifs macro et responsabilités claires.'],
  ['Cadres', 'Cadre du comité, cadre client, cadres départementaux et méthodes réutilisables.'],
  ['Roadmap 2026', 'Gestion de projets centrée sur l’infogérance, les solutions d’affaires et la valeur mesurable.'],
  ['Portée', 'Travaux limités à 75 clients pour les opérations externes et au département TI pour les opérations internes.'],
  ['Clients', 'Présenter rapidement PRIsME à plusieurs clients avec une expérience concrète et crédible.'],
  ['IA & LEX', 'Alimenter les bases de connaissances, soutenir les agents IA et améliorer les habitudes de rigueur.'],
];

const issues = [
  'Temps et roadmap encore ambitieux',
  'Charge de travail à court terme pour le comité',
  'Disponibilité réelle des clients',
  'Adhésion au changement chez BZ',
  'Alimentation continue de LEX',
  'Habitudes de rigueur à renforcer',
];

function GlassCard({
  title,
  icon: Icon,
  children,
  tone = 'slate',
}: {
  title: string;
  icon: ElementType;
  children: ReactNode;
  tone?: 'slate' | 'cyan' | 'emerald' | 'violet' | 'amber';
}) {
  const styles = {
    slate: 'border-white/10 bg-white/[0.055]',
    cyan: 'border-cyan-400/20 bg-cyan-500/10',
    emerald: 'border-emerald-400/20 bg-emerald-500/10',
    violet: 'border-violet-400/20 bg-violet-500/10',
    amber: 'border-amber-400/20 bg-amber-500/10',
  }[tone];

  return (
    <div className={`rounded-3xl border p-5 ${styles}`}>
      <h3 className="mb-3 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-cyan-200" />
        {title}
      </h3>
      <div className="text-sm leading-7 text-slate-200">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function MacroObjectivesPanel() {
  return (
    <GlassCard title="Les 4 objectifs macro" icon={Target} tone="violet">
      <div className="grid gap-3 md:grid-cols-2">
        {macroObjectives.map((objective, index) => (
          <div key={objective} className="rounded-2xl border border-violet-300/20 bg-violet-500/10 p-4">
            <p className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-violet-200">
              Objectif {String(index + 1).padStart(2, '0')}
            </p>
            <p className="text-sm font-bold leading-6 text-white">{objective}</p>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function Metric({ value, label, detail }: { value: string; label: string; detail: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-5">
      <p className="text-4xl font-black text-white md:text-5xl">{value}</p>
      <p className="mt-2 text-sm font-bold uppercase tracking-wide text-cyan-200">{label}</p>
      <p className="mt-2 text-xs leading-5 text-slate-400">{detail}</p>
    </div>
  );
}

function RaciBadge({ value }: { value: string }) {
  if (!value) return <span className="text-slate-600">—</span>;

  const styles: Record<string, string> = {
    R: 'border-cyan-300/40 bg-cyan-400/15 text-cyan-100',
    A: 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100',
    C: 'border-amber-300/40 bg-amber-400/15 text-amber-100',
    I: 'border-violet-300/40 bg-violet-400/15 text-violet-100',
    '?': 'border-slate-400/30 bg-slate-400/10 text-slate-300',
  };

  return (
    <span className={`inline-flex min-w-7 justify-center rounded-full border px-2 py-1 text-xs font-black ${styles[value] ?? styles['?']}`}>
      {value}
    </span>
  );
}

export default function PresentationPRIsMEModal({ isOpen, onClose }: PresentationPRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vision');

  const tabContent = useMemo(() => {
    if (activeTab === 'vision') {
      return (
        <div className="space-y-7">
          <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-white/10 via-cyan-500/10 to-violet-500/10 p-7 md:p-9">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-cyan-200">PRIsME BZ · executive story</p>
            <h3 className="max-w-4xl text-3xl font-black leading-tight text-white md:text-5xl">
              Transformer la rigueur BZ en expérience client mesurable.
            </h3>
            <p className="mt-5 max-w-4xl text-base leading-8 text-slate-200 md:text-lg">
              PRIsME structure les pratiques, l’IA, les processus et la gouvernance pour améliorer les opérations du
              client et les capacités internes de BZ. Le résultat attendu : plus de valeur, plus de visibilité et une
              relation client fondée sur les besoins d’affaires, pas sur la vente opportuniste.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Metric value="4" label="Objectifs macro" detail="Le socle de gouvernance PRIsME 2.0." />
            <Metric value="9" label="Piliers comité TI" detail="Un cadre commun pour décider et exécuter." />
            <Metric value="6+2" label="Cadre client" detail="Six piliers principaux et deux transversales." />
            <Metric value="2026" label="Roadmap" detail="Projet structuré plutôt que suite d’idées." />
          </div>

          <MacroObjectivesPanel />

          <GlassCard title="PRIsME en une phrase" icon={Sparkles} tone="cyan">
            <p className="text-lg font-semibold text-white">
              PRIsME est un ensemble de changements pour améliorer les opérations, la productivité et l’efficacité du
              client et de BZ grâce à l’exploitation judicieuse de l’IA, des pratiques et des processus basés sur la rigueur.
            </p>
          </GlassCard>
        </div>
      );
    }

    if (activeTab === 'essence') {
      return (
        <div className="space-y-7">
          <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-slate-900 via-slate-950 to-cyan-950/50 p-7 md:p-9">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-cyan-200">Ce que PRIsME est vraiment</p>
            <h3 className="max-w-5xl text-3xl font-black leading-tight text-white md:text-5xl">
              Une source de vérité, une méthode de livraison, un cockpit de gouvernance et un moteur de différenciation.
            </h3>
            <p className="mt-5 max-w-4xl text-base leading-8 text-slate-200 md:text-lg">
              Ces quatre idées résument PRIsME sans le réduire à une application. Elles montrent à la fois la structure
              interne, la façon de livrer, la gouvernance et la valeur visible pour le client.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {essenceItems.map((item, index) => (
              <div key={item.title} className={`rounded-3xl border border-white/10 bg-linear-to-br ${item.tone} p-6`}>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-sm font-black text-white">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-xl font-black text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-200">{item.body}</p>
                <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/35 p-4 text-sm font-bold leading-6 text-cyan-100">
                  {item.proof}
                </div>
              </div>
            ))}
          </div>

          <GlassCard title="Message à retenir" icon={Sparkles} tone="cyan">
            <p className="text-lg font-semibold text-white">
              PRIsME n’est pas seulement un outil : c’est la façon dont BZ capture son expertise, la transforme en
              livrables professionnels et démontre une progression concrète au client comme à l’interne.
            </p>
          </GlassCard>
        </div>
      );
    }

    if (activeTab === 'raci') {
      return (
        <div className="space-y-7">
          <GlassCard title="Matrice RACI du comité PRIsME" icon={Users} tone="cyan">
            <p className="mb-4">
              La présentation ne parle pas seulement de vision : elle formalise aussi qui décide, qui réalise, qui est
              consulté et qui doit être informé. Cette matrice rend la gouvernance exploitable.
            </p>
            <div className="grid gap-3 md:grid-cols-5">
              {[
                ['R', 'Responsible', 'Réalise ou porte l’action'],
                ['A', 'Accountable', 'Assume la décision finale'],
                ['C', 'Consulted', 'Contribue ou valide'],
                ['I', 'Informed', 'Doit être tenu au courant'],
                ['?', 'À clarifier', 'Rôle à confirmer'],
              ].map(([letter, label, desc]) => (
                <div key={letter} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                  <RaciBadge value={letter} />
                  <p className="mt-2 font-bold text-white">{label}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">{desc}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-5">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-200">Tableau extrait du PPT</p>
                <h3 className="mt-2 text-xl font-black text-white">Tâches / activités et responsabilités</h3>
              </div>
              <p className="max-w-xl text-xs leading-5 text-slate-400">
                Vue large avec défilement horizontal pour conserver toute la richesse de la matrice originale.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-left text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 rounded-l-2xl border-y border-l border-white/10 bg-slate-950 px-3 py-3 text-slate-300">
                      Tâches / activités
                    </th>
                    {raciRoles.map((role, index) => (
                      <th
                        key={role}
                        className={`border-y border-white/10 bg-slate-950 px-3 py-3 text-slate-300 ${index === raciRoles.length - 1 ? 'rounded-r-2xl border-r' : ''}`}
                      >
                        {role}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {raciRows.map(([task, ...cells]) => (
                    <tr key={task}>
                      <td className="sticky left-0 z-10 border-b border-white/5 bg-[#05070d] px-3 py-3 font-bold text-white">
                        {task}
                      </td>
                      {cells.map((cell, index) => (
                        <td key={`${task}-${raciRoles[index]}`} className="border-b border-white/5 px-3 py-3 text-center">
                          <RaciBadge value={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'cadre') {
      return (
        <div className="space-y-7">
          <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-slate-900 via-slate-950 to-cyan-950/50 p-6 md:p-8">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-cyan-200">Gouvernance & cadre</p>
            <h3 className="max-w-4xl text-2xl font-black leading-tight text-white md:text-4xl">
              Un système simple : la gouvernance décide, le cadre structure, la roadmap exécute.
            </h3>
            <p className="mt-4 max-w-4xl text-sm leading-7 text-slate-300 md:text-base">
              Cet onglet regroupe les deux morceaux qui doivent travailler ensemble. La gouvernance PRIsME 2.0 donne le
              mandat, les rôles, les priorités et les décisions. Le cadre référentiel client 6+2 donne la grille commune
              pour analyser la maturité, organiser les besoins et transformer l’analyse en livrables concrets.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <GlassCard title="Gouvernance PRIsME 2.0" icon={Users} tone="cyan">
              <p>
                Le comité PRIsME agit comme organe de pilotage. Il clarifie qui décide, qui exécute, ce qui est prioritaire,
                ce qui est bloqué et ce qui doit être mesuré pour garder PRIsME cohérent.
              </p>
            </GlassCard>
            <GlassCard title="Cadre référentiel client 6+2" icon={Compass} tone="violet">
              <p>
                Le cadre client donne une structure stable aux diagnostics, aux prises de besoins, aux plans annuels, aux
                plans 5 ans, aux ODS et aux preuves de progression présentées au client.
              </p>
            </GlassCard>
          </div>

          <GlassCard title="Les 9 piliers de la Gouvernance PRIsME 2.0" icon={Layers3} tone="cyan">
            <div className="grid gap-3 md:grid-cols-3">
              {governancePillars.map((pillar, index) => (
                <div key={pillar} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                  <p className="mb-2 text-xs font-black text-cyan-200">{String(index + 1).padStart(2, '0')}</p>
                  <p className="text-sm font-bold leading-6 text-white">{pillar}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard title="Les 6 piliers du cadre référentiel client" icon={Compass} tone="violet">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {clientFrameworkPillars.map((pillar) => (
                <div key={pillar.number} className={`rounded-2xl border border-white/10 bg-linear-to-br ${pillar.tone} p-4`}>
                  <p className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-cyan-100">{pillar.number}</p>
                  <p className="text-base font-black leading-6 text-white">{pillar.title}</p>
                  <p className="mt-2 text-xs leading-5 text-slate-300">{pillar.detail}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard title="Les 2 dimensions transversales" icon={ArrowRight} tone="emerald">
            <div className="grid gap-4 md:grid-cols-2">
              {clientFrameworkTransversals.map((transversal) => (
                <div key={transversal.letter} className={`rounded-2xl border p-5 ${transversal.tone}`}>
                  <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/10 text-sm font-black">
                    {transversal.letter}
                  </div>
                  <p className="text-base font-black text-white">{transversal.title}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{transversal.detail}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard title="Logique cadre · objectif · tâche" icon={ArrowRight} tone="emerald">
            <p>
              Chaque tâche ou activité doit être rattachée à un pilier, à une décision de gouvernance et à un livrable ou
              indicateur visible. Cette relation rend la roadmap défendable : on ne fait pas une tâche parce qu’elle est
              intéressante, on la fait parce qu’elle contribue à la structure PRIsME et à la valeur démontrable.
            </p>
          </GlassCard>
        </div>
      );
    }

    if (activeTab === 'client') {
      return (
        <div className="space-y-7">
          <GlassCard title="Ce que PRIsME devient pour le client" icon={Compass} tone="cyan">
            <p className="text-lg font-semibold text-white">
              Un partenariat commercial novateur et mutuellement bénéfique, piloté par un directeur TI qui agit comme
              conseiller impartial, sans pression de vente, avec une stratégie 5 ans.
            </p>
          </GlassCard>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {clientJourney.map((item) => (
              <div key={item.title} className="rounded-3xl border border-white/10 bg-white/[0.055] p-5">
                <p className="mb-2 text-lg font-black text-white">{item.title}</p>
                <p className="text-sm leading-6 text-slate-300">{item.body}</p>
              </div>
            ))}
          </div>

          <GlassCard title="La photo du client" icon={BarChart3} tone="violet">
            <p>
              La photo de maturité permet de montrer l’évolution au client : priorités visibles, progrès mesurables,
              arbitrages mieux compris et budget TI relié aux besoins d’affaires.
            </p>
          </GlassCard>

          <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <GlassCard title="Approche de prise de besoin" icon={CalendarClock} tone="emerald">
              <BulletList
                items={[
                  '3 à 5 rencontres de 2 heures maximum sur une fenêtre de 6 semaines.',
                  'Approche centrée sur les besoins d’affaires prioritaires et les contraintes budgétaires / temporelles.',
                  'Même méthode, même discours des directeurs TI, mêmes matériels et documentation.',
                  'Questionnaire de prise de besoins d’affaires et questionnaire de maturité des piliers.',
                ]}
              />
            </GlassCard>
            <GlassCard title="Livrables client annoncés" icon={Layers3} tone="cyan">
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  'Capture holistique et priorisée des besoins d’affaires',
                  'Tableau Besoins · Solutions · Coûts',
                  'Feuille de route annuelle',
                  'Plan directeur TI 5 ans',
                  'ODS',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-white/10 bg-slate-950/40 p-3 font-semibold text-white">
                    {item}
                  </div>
                ))}
              </div>
            </GlassCard>
          </div>

          <GlassCard title="Transformation numérique et IA" icon={Brain} tone="amber">
            <BulletList
              items={[
                'Vulgariser l’avancement et la portée des solutions en intelligence artificielle.',
                'Identifier les tâches et processus simples, répétitifs ou automatisables.',
                'Soutenir les employés et décideurs pour augmenter performance et efficacité.',
                'Discuter des subventions et crédits disponibles aux niveaux fédéral, provincial et municipal.',
              ]}
            />
          </GlassCard>
        </div>
      );
    }

    if (activeTab === 'bz') {
      return (
        <div className="space-y-7">
          <GlassCard title="Ce que PRIsME devient pour BZ" icon={BriefcaseBusiness} tone="emerald">
            <BulletList items={bzCapabilities} />
          </GlassCard>

          <div className="grid gap-4 md:grid-cols-3">
            <GlassCard title="Rigueur" icon={CheckCircle2} tone="cyan">
              <p>Standardiser les livrables, les rencontres, les décisions et les suivis.</p>
            </GlassCard>
            <GlassCard title="Connaissance" icon={Brain} tone="violet">
              <p>Alimenter LEX et les agents IA avec une base plus fiable, mieux structurée et réutilisable.</p>
            </GlassCard>
            <GlassCard title="Adoption" icon={Rocket} tone="amber">
              <p>Changer les habitudes internes pour que PRIsME devienne une façon de travailler, pas un projet parallèle.</p>
            </GlassCard>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-7">
        <GlassCard title="Roadmap 2026" icon={CalendarClock} tone="cyan">
          <div className="space-y-3">
            {roadmapItems.map(([title, body]) => (
              <div key={title} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                <p className="text-sm font-black text-white">{title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-300">{body}</p>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard title="Enjeux à maîtriser" icon={Target} tone="amber">
          <div className="grid gap-2 md:grid-cols-2">
            {issues.map((issue) => (
              <div key={issue} className="flex gap-2 rounded-2xl border border-white/10 bg-slate-950/40 p-3">
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-amber-200" />
                <span>{issue}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    );
  }, [activeTab]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-[#05070d] shadow-2xl"
          >
            <header className="relative overflow-hidden border-b border-white/10 px-6 py-7 md:px-9">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(34,211,238,0.18),transparent_34%),radial-gradient(circle_at_85%_20%,rgba(168,85,247,0.18),transparent_30%)]" />
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 z-10 rounded-full border border-white/10 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative max-w-5xl">
                <h2 className="text-3xl font-black leading-tight text-white md:text-6xl">Présentation PRIsME</h2>
              </div>
            </header>

            <div className="flex gap-2 overflow-x-auto border-b border-white/10 bg-slate-950/80 px-4 py-3 md:px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
                      active
                        ? 'border-cyan-300/40 bg-cyan-400/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <main className="flex-1 overflow-y-auto px-5 py-6 md:px-8">{tabContent}</main>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
